import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, CircularProgress, FormControl,
  Grid, IconButton, InputLabel, MenuItem, Select, Tooltip, Typography, makeStyles,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import RefreshIcon from '@material-ui/icons/Refresh';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import { hostname } from '../providers/api';
import { useTokenOps } from '../components/TokenContext';
import { formatDate, formatMoney, formatPercent } from './commercialFormatters';
import { formatMonthTick, monthTickIndexes } from './commercialChartState';
import { buildOverviewQueues } from './commercialNavigationState';

const useStyles = makeStyles(theme => ({
  page: {
    boxSizing: 'border-box', padding: theme.spacing(3), maxWidth: '100%', minWidth: 0,
    overflow: 'hidden', width: '100%',
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(2) },
  },
  header: {
    display: 'flex', justifyContent: 'space-between', gap: theme.spacing(2),
    alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: theme.spacing(2),
  },
  headerActions: {
    display: 'flex', gap: theme.spacing(1), alignItems: 'center',
    [theme.breakpoints.down('xs')]: { width: '100%', '& .MuiFormControl-root': { flex: 1 } },
  },
  notice: {
    alignItems: 'center', display: 'flex', gap: theme.spacing(1), padding: theme.spacing(1.5, 2),
    border: `1px solid ${theme.palette.warning.main}`, background: theme.palette.warning.light,
    color: theme.palette.warning.contrastText, borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  queueSection: { marginBottom: theme.spacing(2) },
  queueHeading: { marginBottom: theme.spacing(.25) },
  queueIntroduction: { marginBottom: theme.spacing(1.5) },
  attention: {
    color: 'inherit', cursor: 'pointer', display: 'block', height: '100%',
    textDecoration: 'none', borderTop: '3px solid transparent',
    transition: theme.transitions.create(['border-color', 'box-shadow', 'transform'], { duration: theme.transitions.duration.shortest }),
    '&:hover': { boxShadow: theme.shadows[3], transform: 'translateY(-1px)' },
    '&:focus': { boxShadow: `0 0 0 3px ${theme.palette.primary.light}`, outline: 'none' },
  },
  attentionDanger: { borderTopColor: theme.palette.error.main },
  attentionWarn: { borderTopColor: theme.palette.warning.main },
  attentionGood: { borderTopColor: theme.palette.success.main },
  queueAction: { color: theme.palette.primary.main, display: 'block', fontWeight: 600, marginTop: theme.spacing(1.5) },
  kpi: { height: '100%' },
  kpiLabel: { display: 'flex', alignItems: 'center', gap: 3, color: theme.palette.text.secondary },
  positive: { color: theme.palette.success.dark },
  negative: { color: theme.palette.error.main },
  muted: { color: theme.palette.text.secondary },
  chartCard: { marginTop: theme.spacing(2), minWidth: 0, overflow: 'hidden' },
  chartViewport: { maxWidth: '100%', overflowX: 'auto', paddingBottom: theme.spacing(.5) },
  chartCanvas: { minWidth: 700 },
  chart: {
    display: 'flex', alignItems: 'flex-end', height: 220, gap: 5,
    paddingTop: theme.spacing(4), borderBottom: `1px solid ${theme.palette.divider}`,
  },
  barWrap: {
    flex: 1, minWidth: 8, height: '100%', display: 'flex', flexDirection: 'column',
    justifyContent: 'flex-end', alignItems: 'center',
  },
  barValue: { fontSize: 10, color: theme.palette.text.secondary, marginBottom: 3, whiteSpace: 'nowrap' },
  bar: {
    width: '100%', minHeight: 3, background: theme.palette.primary.main,
    borderRadius: '3px 3px 0 0', transition: 'opacity .15s',
    '&:hover, &:focus': { opacity: .72, outline: `2px solid ${theme.palette.primary.dark}` },
  },
  partialBar: { background: theme.palette.warning.main },
  axis: { display: 'flex', gap: 5, marginTop: 6 },
  axisLabel: {
    flex: 1, minWidth: 0, textAlign: 'center', fontSize: 10,
    color: theme.palette.text.secondary, overflow: 'visible', whiteSpace: 'nowrap',
  },
}));

const HelpLabel = ({ label, help }) => <span style={{ display: 'inline-flex', alignItems: 'center' }}>
  {label}
  <Tooltip title={help} arrow>
    <IconButton size="small" aria-label={`${label} definition`}><InfoOutlinedIcon style={{ fontSize: 15 }} /></IconButton>
  </Tooltip>
</span>;

const Kpi = ({ label, help, value, change, tone }) => {
  const classes = useStyles();
  const changeClass = tone === 'positive' ? classes.positive : tone === 'negative' ? classes.negative : classes.muted;
  return <Card className={classes.kpi} variant="outlined"><CardContent>
    <Typography variant="body2" className={classes.kpiLabel}><HelpLabel label={label} help={help} /></Typography>
    <Typography variant="h5">{value}</Typography>
    {change && <Typography variant="caption" className={changeClass}>{change}</Typography>}
  </CardContent></Card>;
};

export default function CommercialDashboard() {
  const classes = useStyles();
  const token = useTokenOps().account.accessToken;
  const [range, setRange] = useState(18);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(`${hostname}/commercial/dashboard?range=${range}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(response => response.ok ? response.json() : Promise.reject(new Error(`Dashboard request failed (${response.status})`)))
      .then(setDashboard)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [range, token]);

  useEffect(load, [load]);

  if (loading && !dashboard) return <Box p={6} textAlign="center"><CircularProgress /><Typography>Loading commercial data…</Typography></Box>;
  if (error && !dashboard) return <Box p={4}><Card><CardContent>
    <Typography color="error" gutterBottom>Commercial data could not be loaded</Typography>
    <Typography paragraph>{error.message}</Typography>
    <Button onClick={load} startIcon={<RefreshIcon />}>Retry</Button>
  </CardContent></Card></Box>;
  if (!dashboard) return null;

  const { summary, attention = {}, trend = [], pipeline = [] } = dashboard;
  const maxMrr = Math.max(...trend.map(row => Number(row.mrr)), 1);
  const latestMonth = trend[trend.length - 1]?.month;
  const monthTicks = new Set(monthTickIndexes(trend.length, 10));
  const queues = buildOverviewQueues(attention, latestMonth);
  const pipelineCount = pipeline
    .filter(row => row.isOpen !== false)
    .reduce((total, row) => total + Number(row.opportunities || 0), 0);

  return <main className={classes.page}>
    <header className={classes.header}>
      <div>
        <Typography variant="h4">Commercial overview</Typography>
        <Typography color="textSecondary">See what needs action now, then review recurring-revenue health and momentum.</Typography>
        {dashboard.lastImportedAt && <Typography variant="caption" color="textSecondary">Data updated {formatDate(dashboard.lastImportedAt)}</Typography>}
      </div>
      <div className={classes.headerActions}>
        <FormControl variant="outlined" size="small">
          <InputLabel id="range-label">Range</InputLabel>
          <Select labelId="range-label" value={range} onChange={event => setRange(event.target.value)} label="Range">
            <MenuItem value={6}>6 months</MenuItem>
            <MenuItem value={12}>12 months</MenuItem>
            <MenuItem value={18}>18 months</MenuItem>
            <MenuItem value="ALL">All history</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title="Reload commercial data"><IconButton onClick={load} aria-label="Refresh commercial data"><RefreshIcon /></IconButton></Tooltip>
      </div>
    </header>

    {dashboard.partialMonths?.length > 0 && <div className={classes.notice}>
      <WarningRoundedIcon fontSize="small" />
      <span><strong>{dashboard.partialMonths.join(', ')}</strong> contains partial data. Comparisons and chart styling identify incomplete periods.</span>
    </div>}

    <section className={classes.queueSection} aria-labelledby="commercial-attention-title">
      <Typography id="commercial-attention-title" className={classes.queueHeading} variant="h6">Needs attention</Typography>
      <Typography className={classes.queueIntroduction} variant="body2" color="textSecondary">Each queue opens in the workspace where the follow-up is completed.</Typography>
      <Grid container spacing={2}>
        {queues.map(queue => <Grid item xs={12} md={4} key={queue.key}>
          <Card
            component="a"
            href={queue.href}
            className={`${classes.attention} ${queue.tone === 'danger' ? classes.attentionDanger : queue.tone === 'warning' ? classes.attentionWarn : classes.attentionGood}`}
            variant="outlined"
          >
            <CardContent>
              <Typography variant="h6">{queue.title}</Typography>
              <Typography color="textSecondary">{queue.amount !== undefined ? `${formatMoney(queue.amount)} ${queue.description}` : queue.description}</Typography>
              <Typography className={classes.queueAction} variant="body2">{queue.action} →</Typography>
            </CardContent>
          </Card>
        </Grid>)}
      </Grid>
    </section>

    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md><Kpi label="Current MRR" help="Recurring revenue in the latest imported month." value={formatMoney(summary.currentMrr)} change={`${Number(summary.mrrDelta) >= 0 ? '+' : ''}${formatMoney(summary.mrrDelta)} · ${summary.mrrDeltaPercentage ? formatPercent(summary.mrrDeltaPercentage) : 'No prior baseline'}`} tone={Number(summary.mrrDelta) >= 0 ? 'positive' : 'negative'} /></Grid>
      <Grid item xs={12} sm={6} md><Kpi label="ARR run rate" help="Current MRR multiplied by 12; this is not invoiced revenue." value={formatMoney(summary.currentArr)} /></Grid>
      <Grid item xs={12} sm={6} md><Kpi label="Active customers" help="Customers with active recurring revenue in the latest period." value={summary.activeCustomers} change={`${summary.customerCount} total customers`} /></Grid>
      <Grid item xs={12} sm={6} md><Kpi label="Net revenue retention" help="Starting MRR plus expansion, less contraction and churn, divided by starting MRR." value={summary.nrr ? formatPercent(summary.nrr) : 'Not available'} tone={Number(summary.nrr) >= 100 ? 'positive' : 'negative'} /></Grid>
      <Grid item xs={12} sm={6} md><Kpi label="Weighted pipeline" help="Potential ARR multiplied by each opportunity probability." value={formatMoney(summary.weightedPipeline)} change={`${pipelineCount} opportunities`} /></Grid>
    </Grid>

    <Card className={classes.chartCard} variant="outlined"><CardContent>
      <Typography variant="h6">MRR evolution</Typography>
      <Typography variant="body2" color="textSecondary">Monthly recurring revenue. Hover or focus a bar for its exact value; scroll horizontally when viewing long histories.</Typography>
      <div className={classes.chartViewport}>
        <div className={classes.chartCanvas} style={{ minWidth: Math.max(700, trend.length * 18) }}>
          <div className={classes.chart}>
            {trend.map((row, index) => {
              const showValue = index === trend.length - 1 || index % Math.max(1, Math.ceil(trend.length / 6)) === 0;
              const tooltip = `${row.month}: ${formatMoney(row.mrr)} · ${row.customers} active customers${row.isPartial ? ' · partial period' : ''}`;
              return <div className={classes.barWrap} key={row.month}>
                {showValue && <span className={classes.barValue}>{formatMoney(row.mrr)}</span>}
                <Tooltip arrow title={tooltip}>
                  <div
                    tabIndex={0}
                    role="img"
                    aria-label={`${row.month}, ${formatMoney(row.mrr)}, ${row.customers} customers${row.isPartial ? ', partial' : ''}`}
                    className={`${classes.bar} ${row.isPartial ? classes.partialBar : ''}`}
                    style={{ height: `${Math.max(3, Number(row.mrr) / maxMrr * 100)}%` }}
                  />
                </Tooltip>
              </div>;
            })}
          </div>
          <div className={classes.axis}>{trend.map((row, index) => <span className={classes.axisLabel} key={row.month}>{monthTicks.has(index) ? formatMonthTick(row.month) : ''}</span>)}</div>
        </div>
      </div>
    </CardContent></Card>
  </main>;
}
